import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
const MAX_PDF_BYTES = 75 * 1024 * 1024;
const POLL_INTERVAL_MS = 4000;
const isActive = (entry) => ['pending', 'running'].includes(entry.pdf_download?.status);

function pollingDelay(entries) {
  const delays = entries.filter((entry) => entry.can_manage_documents && isActive(entry)).map((entry) => {
    const download = entry.pdf_download;
    if (download.status === 'running' || download.manual_retry_queued) return POLL_INTERVAL_MS;
    const retryAt = Date.parse(download.next_attempt_at);
    return Number.isFinite(retryAt) ? Math.min(30000, Math.max(POLL_INTERVAL_MS, retryAt - Date.now())) : POLL_INTERVAL_MS;
  });
  return delays.length ? Math.min(...delays) : POLL_INTERVAL_MS;
}

function requestHeaders() {
  // The shared session interceptor refreshes this bearer token when necessary.
  const token = localStorage.getItem('accessToken') || localStorage.getItem('clientToken');
  return { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

async function responsePayload(response, fallback) {
  let data;
  try {
    data = await response.json();
  } catch {
    throw new Error(`${fallback} (${response.status}). Please try again.`);
  }
  if (!response.ok && !(response.status === 409 && data?.docket)) {
    const detail = typeof data?.detail === 'string' ? data.detail : null;
    const fileError = Array.isArray(data?.file) ? data.file.join(' ') : data?.file;
    throw new Error(detail || (typeof fileError === 'string' ? fileError : fallback));
  }
  return data;
}

function DownloadStatus({ entry }) {
  const download = entry.pdf_download;
  if (!download) return null;
  const labels = {
    pending: 'PDF download queued. It will update here automatically.',
    running: 'Downloading PDF from the court…',
    succeeded: 'Court PDF download complete.',
    failed: 'The court PDF could not be downloaded.',
    blocked: 'The court PDF needs attention. You can upload a saved PDF below.',
  };
  const needsAttention = ['failed', 'blocked'].includes(download.status);
  const retryTime = Date.parse(download.next_attempt_at);
  return (
    <div className={`rounded-md px-3 py-2 text-sm ${needsAttention ? 'bg-amber-50 text-amber-900' : 'bg-slate-50 text-slate-700'}`} role="status" aria-live="polite">
      <p>{labels[download.status] || 'Court PDF status is unavailable.'}</p>
      {(needsAttention || download.status === 'pending') && download.last_error && <p className="mt-1 break-words">{download.last_error}</p>}
      {download.status === 'pending' && Number.isFinite(retryTime) && <p className="mt-1">Automatic retry: {new Date(retryTime).toLocaleString()}.</p>}
    </div>
  );
}

function DocketCard({ entry, caseId, onMutationStart, onDocketChange }) {
  const [showUpload, setShowUpload] = useState(false);
  const [file, setFile] = useState(null);
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const mounted = useRef(true);
  const submitting = useRef(false);
  const fileInput = useRef(null);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const documents = Array.isArray(entry.documents) ? entry.documents : [];
  const active = isActive(entry);
  const canManage = entry.can_manage_documents === true;
  const headingId = `docket-${entry.id}-heading`;

  const submit = async (kind) => {
    if (submitting.current) return;
    if (kind === 'upload' && !file) {
      setError('Choose a PDF to attach.');
      return;
    }
    submitting.current = true;
    onMutationStart();
    setBusy(kind);
    setError('');
    setNotice('');
    try {
      const upload = kind === 'upload';
      const body = upload ? new FormData() : JSON.stringify({});
      if (upload) {
        body.append('file', file);
        if (description.trim()) body.append('description', description.trim());
      }
      const response = await fetch(`${API_URL}/case/${caseId}/dockets/${entry.id}/${upload ? 'documents' : 'retry-pdf'}/`, {
        method: 'POST',
        headers: { ...requestHeaders(), ...(!upload ? { 'Content-Type': 'application/json' } : {}) },
        body,
      });
      const data = await responsePayload(response, upload ? 'Could not attach the PDF.' : 'Could not queue the court PDF download.');
      if (!data?.docket || String(data.docket.id) !== String(entry.id)) {
        throw new Error('The server did not return the updated docket. Please refresh the dockets before trying again.');
      }
      if (!mounted.current) return;
      onDocketChange(data.docket);
      setNotice(data.detail || (upload ? 'PDF attached.' : 'PDF download queued.'));
      if (upload) {
        setFile(null);
        setDescription('');
        setShowUpload(false);
        if (fileInput.current) fileInput.current.value = '';
      }
    } catch (requestError) {
      if (mounted.current) setError(requestError.message || 'The request could not be completed. Please try again.');
    } finally {
      submitting.current = false;
      if (mounted.current) setBusy('');
    }
  };

  const selectFile = (event) => {
    const selected = event.target.files?.[0] || null;
    setError('');
    setNotice('');
    setFile(null);
    if (selected && !(/\.pdf$/i.test(selected.name) || selected.type === 'application/pdf')) {
      setError('Choose a PDF file. Convert images or other documents to PDF first.');
      event.target.value = '';
      return;
    }
    if (selected && (selected.size === 0 || selected.size > MAX_PDF_BYTES)) {
      setError('Choose a nonempty PDF no larger than 75 MB.');
      event.target.value = '';
      return;
    }
    setFile(selected);
  };

  return (
    <section aria-labelledby={headingId} className="mb-4 rounded-lg bg-white p-4 shadow-md sm:p-6">
      <h2 id={headingId} className="mb-1 text-xl font-semibold">Docket #{entry.docket_number}</h2>
      <p className="mb-2 text-sm text-gray-500">Filed: {entry.filed_at ? new Date(entry.filed_at).toLocaleDateString() : 'Not available'}</p>
      <p className="mb-4 whitespace-pre-wrap break-words text-gray-700">{entry.docket_text}</p>
      <div className="mb-3 flex flex-col items-start gap-2">
        {documents.map((doc) => (
          <a key={doc.id} href={doc.file_url} target="_blank" rel="noopener noreferrer" className="inline-block max-w-full break-words rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700">
            {doc.description || 'Open PDF'}
          </a>
        ))}
        {!documents.length && <p className="text-sm text-slate-500">No PDF is attached to this docket.</p>}
      </div>
      {canManage && <DownloadStatus entry={entry} />}
      {canManage && (
        <div className="mt-3">
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => submit('retry')} disabled={Boolean(busy) || !entry.can_retry_pdf} className="min-h-11 rounded border border-blue-300 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50">
              {busy === 'retry' ? 'Queuing…' : active && !entry.can_retry_pdf ? 'PDF download in progress' : 'Retry court PDF'}
            </button>
            <button type="button" onClick={() => { setShowUpload((value) => !value); setError(''); }} disabled={Boolean(busy)} aria-expanded={showUpload} aria-controls={`docket-${entry.id}-upload`} className="min-h-11 rounded border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
              {showUpload ? 'Close upload' : 'Upload PDF'}
            </button>
          </div>
          {!entry.can_retry_pdf && !active && <p className="mt-2 text-xs text-slate-500">No saved court link is available. You can attach the PDF manually.</p>}
          {showUpload && (
            <form id={`docket-${entry.id}-upload`} onSubmit={(event) => { event.preventDefault(); submit('upload'); }} className="mt-3 space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3">
              <p className="text-sm text-slate-600">Attach a PDF to this docket. Existing attachments will be kept. Maximum size: 75 MB.</p>
              <label className="block text-sm font-medium text-slate-700">
                PDF file
                <input ref={fileInput} type="file" accept="application/pdf,.pdf" onChange={selectFile} disabled={Boolean(busy)} className="mt-1 block w-full max-w-full text-sm" />
              </label>
              <label className="block text-sm font-medium text-slate-700">
                Description (optional)
                <input value={description} onChange={(event) => setDescription(event.target.value)} maxLength={255} disabled={Boolean(busy)} className="mt-1 block w-full rounded border border-slate-300 px-3 py-2 text-base font-normal" placeholder="For example: Signed order" />
              </label>
              <button type="submit" disabled={!file || Boolean(busy)} className="min-h-11 rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">{busy === 'upload' ? 'Attaching…' : 'Attach PDF'}</button>
            </form>
          )}
          {error && <p role="alert" className="mt-2 break-words text-sm text-red-700">{error}</p>}
          {notice && <p role="status" aria-live="polite" className="mt-2 text-sm text-green-700">{notice}</p>}
        </div>
      )}
    </section>
  );
}

export default function CaseDockets() {
  const { caseId } = useParams();
  const [dockets, setDockets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pollError, setPollError] = useState('');
  const generation = useRef(0);
  const mutationVersion = useRef(0);
  const activeCase = useRef(caseId);
  activeCase.current = caseId;
  const hasActiveDownloads = dockets.some((entry) => entry.can_manage_documents && isActive(entry));
  const hasImmediateDownload = dockets.some((entry) => entry.can_manage_documents && (entry.pdf_download?.status === 'running' || entry.pdf_download?.manual_retry_queued));
  const currentDockets = useRef(dockets);
  currentDockets.current = dockets;

  useEffect(() => {
    const currentGeneration = ++generation.current;
    const controller = new AbortController();
    setDockets([]);
    setLoading(true);
    setError('');
    setPollError('');
    fetch(`${API_URL}/case/${caseId}/dockets/`, { headers: requestHeaders(), signal: controller.signal })
      .then((response) => responsePayload(response, 'Could not load the dockets.'))
      .then((data) => {
        if (!Array.isArray(data)) throw new Error('The server returned an invalid docket list.');
        if (generation.current !== currentGeneration || controller.signal.aborted) return;
        setDockets(data);
        setLoading(false);
      })
      .catch((requestError) => {
        if (generation.current !== currentGeneration || controller.signal.aborted) return;
        setError(requestError.message);
        setLoading(false);
      });
    return () => controller.abort();
  }, [caseId]);

  useEffect(() => {
    if (!hasActiveDownloads || loading) return undefined;
    const currentGeneration = generation.current;
    const controller = new AbortController();
    let timer;
    const poll = async () => {
      const version = mutationVersion.current;
      let nextDelay = pollingDelay(currentDockets.current);
      try {
        const response = await fetch(`${API_URL}/case/${caseId}/dockets/`, { headers: requestHeaders(), signal: controller.signal });
        const data = await responsePayload(response, 'Could not update the PDF download status.');
        if (!Array.isArray(data)) throw new Error('Could not update the PDF download status.');
        if (controller.signal.aborted || generation.current !== currentGeneration) return;
        // Do not let an older list response undo an attachment just added here.
        if (mutationVersion.current === version) {
          setDockets(data);
          nextDelay = pollingDelay(data);
        }
        setPollError('');
      } catch (requestError) {
        if (controller.signal.aborted || generation.current !== currentGeneration) return;
        setPollError('The PDF status could not be updated. Your dockets are still here; checking again shortly.');
      }
      if (!controller.signal.aborted) timer = window.setTimeout(poll, nextDelay);
    };
    timer = window.setTimeout(poll, pollingDelay(currentDockets.current));
    return () => { controller.abort(); window.clearTimeout(timer); };
  }, [caseId, hasActiveDownloads, hasImmediateDownload, loading]);

  const updateDocket = (updated, sourceCaseId) => {
    if (activeCase.current !== sourceCaseId) return;
    mutationVersion.current += 1;
    setDockets((current) => current.map((entry) => String(entry.id) === String(updated.id) ? updated : entry));
  };

  if (loading) return <div className="p-4" role="status">Loading dockets…</div>;
  if (error) return <div className="p-4 text-red-600" role="alert">Error: {error}</div>;
  return (
    <div className="container mx-auto p-4">
      <h1 className="mb-4 text-2xl font-bold">Docket Entries</h1>
      {pollError && <p role="status" className="mb-3 rounded bg-amber-50 p-3 text-sm text-amber-900">{pollError}</p>}
      {!dockets.length ? <p className="text-gray-600">No dockets found for this case.</p> : dockets.map((entry) => (
        <DocketCard key={`${caseId}:${entry.id}`} caseId={caseId} entry={entry} onMutationStart={() => { mutationVersion.current += 1; }} onDocketChange={(updated) => updateDocket(updated, caseId)} />
      ))}
    </div>
  );
}
